import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Avatar, Button, Card, Empty, Skeleton, Space, Statistic, Tag, Typography,
} from 'antd';
import { ArrowLeftOutlined, PhoneOutlined } from '@ant-design/icons';
import type {
  LeaveStatus, LeaveType, StaffDetailLeaveRow, StaffDetailPayslip, StaffDetailRequisition,
} from '@velora/contracts';
import { productApi } from '../../../api';

const DAY = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const LEAVE_TONE: Record<LeaveType, string> = {
  casual: 'blue', sick: 'volcano', earned: 'green', unpaid: 'default', other: 'purple',
};
const STATUS_TONE: Record<LeaveStatus, string> = {
  pending: 'processing', approved: 'success', rejected: 'error', cancelled: 'default',
};

/**
 * Admin — Staff profile. Aggregate read: profile + weekly load + leave +
 * payroll + submitted requests. Not editable here — HR settings owns edits.
 */
export function StaffDetailPage() {
  const { institution = '', code = '' } = useParams<{ institution: string; code: string }>();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['staff-detail', institution, code],
    queryFn: () => productApi.getStaffDetail(institution, code),
    enabled: !!institution && !!code,
  });
  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 8 }} />;
  const d = q.data;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Button icon={<ArrowLeftOutlined />} type="text" size="small" onClick={() => navigate(`/i/${institution}/admin/staff`)}>Back</Button>
      </div>

      <Card size="small">
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <Avatar size={56} style={{ background: '#2f6ab0', fontSize: 18 }}>
            {d.staff.name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
          </Avatar>
          <div style={{ flex: 1 }}>
            <Typography.Title level={3} style={{ margin: 0 }}>{d.staff.name}</Typography.Title>
            <div style={{ color: '#5b6478', fontSize: 12 }}>
              <Tag>{d.staff.role.replace(/_/g, ' ')}</Tag>
              {d.staff.status !== 'active' && <Tag color="warning">{d.staff.status}</Tag>}
              <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
                <PhoneOutlined /> {d.staff.phone}
                {d.staff.email && ` · ${d.staff.email}`}
              </span>
            </div>
          </div>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="Weekly periods" value={d.weekly_period_count} /></Card>
        <Card size="small"><Statistic title="Sections" value={d.distinct_sections} /></Card>
        <Card size="small"><Statistic title="Homework authored" value={d.homework_authored} /></Card>
        <Card size="small"><Statistic title="Covering upcoming" value={d.substitutions_covering_upcoming} /></Card>
      </div>

      <TimetableCard slots={d.weekly_slots} />

      <Card size="small" title={`Leave · ${d.leave.pending} pending · ${d.leave.approved} approved`}>
        {d.leave.recent.length === 0
          ? <Empty description="No leave requests yet." style={{ padding: 20 }} />
          : (
            <div style={{ display: 'grid', gap: 6 }}>
              {d.leave.recent.map((r) => <LeaveRow key={r.id} r={r} />)}
            </div>
          )}
      </Card>

      <Card size="small" title="Payroll">
        {!d.payroll.has_salary_structure && (
          <Alert type="warning" showIcon style={{ marginBottom: 8 }}
            message="No salary structure set" description="Add one at HR → Payroll → Structure before running the next month." />
        )}
        {d.payroll.recent.length === 0
          ? <Empty description="No payslips yet." style={{ padding: 20 }} />
          : (
            <div style={{ display: 'grid', gap: 6 }}>
              {d.payroll.recent.map((p) => <PayslipRow key={p.month} p={p} />)}
            </div>
          )}
      </Card>

      <Card size="small" title={`Requests submitted (${d.requisitions.total})`}>
        {d.requisitions.recent.length === 0
          ? <Empty description="No purchase requests submitted." style={{ padding: 20 }} />
          : (
            <div style={{ display: 'grid', gap: 6 }}>
              {d.requisitions.recent.map((r) => <RequestRow key={r.code} r={r} institution={institution} />)}
            </div>
          )}
      </Card>

      <Alert
        type="info" showIcon
        message="Docs & observations"
        description="Certification docs and classroom observations live in the HR file — coming with the People/HR settings module."
      />
    </Space>
  );
}

function TimetableCard({ slots }: { slots: import('@velora/contracts').StaffDetailSlot[] }) {
  const byDay = useMemo(() => {
    const m = new Map<number, typeof slots>();
    for (const s of slots) {
      const arr = m.get(s.day_of_week) ?? [];
      arr.push(s);
      m.set(s.day_of_week, arr);
    }
    return m;
  }, [slots]);
  if (slots.length === 0) {
    return (
      <Card size="small" title="Weekly load">
        <Empty description="No periods on the timetable yet." style={{ padding: 20 }} />
      </Card>
    );
  }
  return (
    <Card size="small" title="Weekly load">
      <div style={{ display: 'grid', gap: 8 }}>
        {[1, 2, 3, 4, 5, 6].map((d) => {
          const list = (byDay.get(d) ?? []).sort((a, b) => a.period - b.period);
          if (list.length === 0) return null;
          return (
            <div key={d}>
              <Typography.Text style={{ fontSize: 11, color: '#8a94a6', letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 600 }}>
                {DAY[d]} · {list.length}
              </Typography.Text>
              <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {list.map((s, i) => (
                  <Tag key={i} color="processing">
                    P{s.period} · {s.subject_name} · {s.grade}{s.section ? `-${s.section}` : ''}
                  </Tag>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function LeaveRow({ r }: { r: StaffDetailLeaveRow }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '4px 0', borderTop: '1px solid #f1f1f4', fontSize: 12.5 }}>
      <Tag color={LEAVE_TONE[r.leave_type]}>{r.leave_type}</Tag>
      <b>{r.from_date}</b>→<b>{r.to_date}</b>
      <span style={{ color: '#5b6478' }}>· {r.days}d</span>
      <div style={{ flex: 1 }} />
      <Tag color={STATUS_TONE[r.status]}>{r.status}</Tag>
    </div>
  );
}

function PayslipRow({ p }: { p: StaffDetailPayslip }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '4px 0', borderTop: '1px solid #f1f1f4', fontSize: 12.5 }}>
      <b style={{ minWidth: 70 }}>{p.month}</b>
      <Tag color={p.status === 'paid' ? 'success' : p.status === 'locked' ? 'processing' : 'default'}>{p.status}</Tag>
      <div style={{ flex: 1 }} />
      <span style={{ color: '#8a94a6' }}>gross ₹{paise(p.gross_paise)}</span>
      <b>net ₹{paise(p.net_paise)}</b>
    </div>
  );
}

function RequestRow({ r, institution }: { r: StaffDetailRequisition; institution: string }) {
  return (
    <Link to={`/i/${institution}/admin/expenses/requisitions`} style={{ color: '#14213d', textDecoration: 'none' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '4px 0', borderTop: '1px solid #f1f1f4', fontSize: 12.5 }}>
        <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{r.code}</span>
        <b>{r.subject}</b>
        <div style={{ flex: 1 }} />
        <Tag>{r.status}</Tag>
        <span>₹{paise(r.amount_estimate_paise)}</span>
      </div>
    </Link>
  );
}

function paise(n: number): string {
  return (n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}
